import type { ReactNode } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from './lib/auth.tsx';
import { t } from './lib/i18n.ts';
import { Spinner, cls } from './lib/ui.tsx';
import { SolfoLogo } from './components/Logo.tsx';
import NotificationCenter from './components/NotificationCenter.tsx';
import { IconCalendar, IconChat, IconGear, IconMic, IconSun, IconTrend } from './components/icons.tsx';

function coachNav() {
  return [
    { to: '/coach', label: t('nav.dashboard', 'Dashboard'), end: true },
    { to: '/coach/review', label: t('nav.review', 'Review queue') },
    { to: '/coach/exercises', label: t('nav.exercises', 'Exercises') },
    { to: '/coach/templates', label: t('nav.templates', 'Templates') },
    { to: '/coach/messages', label: t('nav.messages', 'Messages') },
    { to: '/coach/calendar', label: t('nav.calendar', 'Calendar') },
    { to: '/coach/billing', label: t('nav.billing', 'Billing') },
    { to: '/coach/studio', label: t('nav.studio', 'Studio') },
    { to: '/coach/marketplace', label: 'Marketplace' },
    { to: '/coach/settings', label: t('nav.settings', 'Settings') },
  ];
}

export function CoachLayout() {
  const { me, loading, logout } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role !== 'coach') return <Navigate to="/app" replace />;

  return (
    <div className="min-h-dvh lg:flex">
      <aside className="hidden w-56 shrink-0 border-r border-slate-200 bg-white lg:block">
        <div className="sticky top-0 p-4">
          <Link to="/coach" className="mb-6 block px-2">
            <SolfoLogo size={26} />
          </Link>
          <nav className="space-y-0.5">
            {coachNav().map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cls(
                    'block rounded-xl px-3 py-2 text-sm font-medium transition-all duration-150',
                    isActive
                      ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-md shadow-brand-600/25'
                      : 'text-slate-600 hover:translate-x-0.5 hover:bg-brand-50 hover:text-brand-700',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-40 bg-white/90 backdrop-blur">
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-2 lg:hidden">
              <Link to="/coach"><SolfoLogo size={22} /></Link>
            </div>
            <div className="hidden text-sm text-slate-500 lg:block">{me.name}</div>
            <div className="flex items-center gap-1">
              <NotificationCenter role="coach" />
              <button onClick={logout} className="rounded-lg px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100">
                Log out
              </button>
            </div>
          </div>
          <div aria-hidden className="h-px bg-gradient-to-r from-brand-500 via-brand-300/60 to-transparent" />
        </header>
        {/* J7 · mobile companion nav for the review-queue-from-the-sofa case */}
        <nav className="scrollbar-none flex gap-1 overflow-x-auto border-b border-slate-200 bg-white px-2 py-1.5 lg:hidden">
          {coachNav().map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cls(
                  'rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors',
                  isActive
                    ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-white shadow-sm shadow-brand-600/30'
                    : 'bg-slate-100 text-slate-600 hover:bg-brand-50 hover:text-brand-700',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <main className="animate-fade-up mx-auto max-w-6xl p-4" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function StudentLayout() {
  const { me, loading, logout } = useAuth();
  const location = useLocation();
  if (loading) return <Spinner />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role !== 'student') return <Navigate to="/coach" replace />;

  const tabs = [
    { to: '/app', label: t('nav.today', 'Today'), icon: IconSun, end: true },
    { to: '/app/progress', label: t('nav.progress', 'Progress'), icon: IconTrend },
    { to: '/app/takes', label: t('nav.takes', 'Takes'), icon: IconMic },
    { to: '/app/messages', label: t('nav.messages', 'Messages'), icon: IconChat },
    { to: '/app/lessons', label: t('nav.lessons', 'Lessons'), icon: IconCalendar },
  ];

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-2.5">
        <Link to="/app"><SolfoLogo size={22} /></Link>
        <div className="flex items-center gap-1">
          <NotificationCenter role="student" />
          <Link to="/app/settings" className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Settings">
            <IconGear />
          </Link>
          <button onClick={logout} className="rounded-lg px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100">
            Log out
          </button>
        </div>
        </div>
        <div aria-hidden className="h-px bg-gradient-to-r from-brand-500 via-brand-300/60 to-transparent" />
      </header>
      <main className="animate-fade-up flex-1 p-4 pb-24" key={location.pathname}>
        <Outlet />
      </main>
      <nav className="fixed inset-x-0 bottom-0 z-40 mx-auto max-w-lg border-t border-brand-100 bg-white/95 shadow-[0_-4px_20px_-12px_rgba(216,90,48,0.25)] backdrop-blur pb-[env(safe-area-inset-bottom)]">
        <div className="flex">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                cls(
                  'group flex flex-1 flex-col items-center gap-0.5 py-1.5 text-[11px] font-medium transition-colors',
                  isActive ? 'text-brand-700' : 'text-slate-400 hover:text-slate-600',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cls(
                      'flex items-center justify-center rounded-full px-3.5 py-1 transition-all duration-200',
                      isActive ? 'bg-gradient-to-b from-brand-100 to-brand-200/70 shadow-inner' : 'bg-transparent',
                    )}
                  >
                    <tab.icon />
                  </span>
                  {tab.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export function PublicShell({ children }: { children?: ReactNode }) {
  const { me } = useAuth();
  return (
    <div className="min-h-dvh bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/"><SolfoLogo size={24} /></Link>
          <nav className="flex items-center gap-3 text-sm">
            <Link to="/coaches" className="text-slate-600 hover:text-brand-700">Find a coach</Link>
            <Link to="/gallery" className="text-slate-600 hover:text-brand-700">Template gallery</Link>
            {me ? (
              <Link to={me.role === 'coach' ? '/coach' : '/app'} className="rounded-lg bg-brand-600 px-3 py-1.5 font-medium text-white">
                Open app
              </Link>
            ) : (
              <Link to="/login" className="rounded-lg bg-brand-600 px-3 py-1.5 font-medium text-white">
                Log in
              </Link>
            )}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-5xl p-4">{children ?? <Outlet />}</main>
    </div>
  );
}
